import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Topbar } from "@/components/parcelio/topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  vehiclesApi,
  directoryApi,
  type Vehicle,
  type VehicleType as ApiVehicleType,
  type DriverDirectoryEntry,
} from "@/lib/api";
import { useAuth } from "@/stores/auth";
import { ArrowLeft, Search, ArrowLeftRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { LoadingBlock } from "@/components/ui/spinner";

export default function AssignmentsPage() {
  const token = useAuth((s) => s.session?.accessToken);

  const [items, setItems] = useState<Vehicle[]>([]);
  const [types, setTypes] = useState<ApiVehicleType[]>([]);
  const [drivers, setDrivers] = useState<DriverDirectoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [vehicleList, typeList, driverList] = await Promise.all([
          vehiclesApi.list(token),
          vehiclesApi.listTypes(),
          directoryApi.drivers(token),
        ]);
        if (cancelled) return;
        setItems(vehicleList);
        setTypes(typeList);
        setDrivers(driverList);
      } catch (err) {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : "Failed to load assignments");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const typesById = useMemo(() => new Map(types.map((t) => [t.id, t])), [types]);
  const driversById = useMemo(() => new Map(drivers.map((d) => [d.id, d])), [drivers]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return items.filter((v) => {
      if (!s) return true;
      const drv = v.current_driver_id ? driversById.get(v.current_driver_id) : undefined;
      return (
        v.plate.toLowerCase().includes(s) ||
        (v.model ?? "").toLowerCase().includes(s) ||
        (drv?.name.toLowerCase().includes(s) ?? false)
      );
    });
  }, [items, q, driversById]);

  const reassign = async (vehicleId: string, driverId: string | undefined) => {
    const v = items.find((x) => x.id === vehicleId);
    if (!v) return;
    const previous = v.current_driver_id;
    setItems((cur) => cur.map((x) => (x.id === vehicleId ? { ...x, current_driver_id: driverId ?? null } : x)));
    try {
      const updated = await vehiclesApi.update(vehicleId, { current_driver_id: driverId ?? null }, token);
      setItems((cur) => cur.map((x) => (x.id === vehicleId ? updated : x)));
      const d = driverId ? driversById.get(driverId)?.name : null;
      toast.success(d ? `${v.plate} assigned to ${d}` : `${v.plate} unassigned`);
    } catch (err) {
      setItems((cur) => cur.map((x) => (x.id === vehicleId ? { ...x, current_driver_id: previous } : x)));
      toast.error(err instanceof Error ? err.message : "Failed to update assignment");
    }
  };

  const assignedCount = items.filter((v) => v.current_driver_id).length;
  const unassignedCount = items.length - assignedCount;

  const driverLoad = useMemo(() => {
    const map = new Map<string, number>();
    items.forEach((v) => {
      if (!v.current_driver_id) return;
      map.set(v.current_driver_id, (map.get(v.current_driver_id) ?? 0) + 1);
    });
    return map;
  }, [items]);

  return (
    <>
      <Topbar
        title="Vehicle assignments"
        subtitle={`${assignedCount} assigned · ${unassignedCount} unassigned`}
        actions={
          <Button variant="outline" size="sm" asChild>
            <Link to="/app/vehicles"><ArrowLeft className="mr-1.5 h-4 w-4" /> Fleet</Link>
          </Button>
        }
      />

      <div className="flex-1 space-y-4 p-4 md:p-6">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="surface-card p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Total vehicles</div>
            <div className="mt-1 text-2xl font-bold">{items.length}</div>
          </div>
          <div className="surface-card p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Assigned</div>
            <div className="mt-1 text-2xl font-bold text-success">{assignedCount}</div>
          </div>
          <div className="surface-card p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Unassigned</div>
            <div className="mt-1 text-2xl font-bold text-warning-foreground">{unassignedCount}</div>
          </div>
        </div>

        <div className="surface-card">
          <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between">
            <div className="relative max-w-sm flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vehicle or driver…" className="pl-9" />
            </div>
            <div className="text-xs text-muted-foreground">
              <ArrowLeftRight className="mr-1 inline h-3 w-3" />
              Pick a driver to instantly reassign.
            </div>
          </div>
          {loading ? (
            <LoadingBlock label="Loading assignments…" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Plate</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Currently assigned</TableHead>
                  <TableHead className="w-[240px]">Assign driver</TableHead>
                  <TableHead className="text-right">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((v) => {
                  const currentDriver = v.current_driver_id ? driversById.get(v.current_driver_id) : undefined;
                  const typeInfo = typesById.get(v.type_id);
                  const title = [v.make, v.model].filter(Boolean).join(" ") || v.plate;
                  return (
                    <TableRow key={v.id}>
                      <TableCell className="font-medium">{title}</TableCell>
                      <TableCell className="font-mono text-xs">{v.plate}</TableCell>
                      <TableCell className="capitalize text-muted-foreground">{typeInfo?.name ?? v.type_id}</TableCell>
                      <TableCell>
                        <span className={cn(
                          "inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize",
                          v.status === "active" && "bg-success/10 text-success border-success/20",
                          v.status === "maintenance" && "bg-warning/15 text-warning-foreground border-warning/30",
                          v.status === "inactive" && "bg-info/10 text-info border-info/20",
                          v.status === "retired" && "bg-muted text-muted-foreground",
                        )}>{v.status}</span>
                      </TableCell>
                      <TableCell>
                        {currentDriver ? (
                          <div className="flex items-center gap-2">
                            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold">
                              {currentDriver.name.slice(0, 2).toUpperCase()}
                            </div>
                            <div className="leading-tight">
                              <div className="text-xs font-medium">{currentDriver.name}</div>
                              <div className="text-[10px] text-muted-foreground">load {driverLoad.get(currentDriver.id) ?? 0}</div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Unassigned</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={v.current_driver_id ?? "none"}
                          onValueChange={(val) => reassign(v.id, val === "none" ? undefined : val)}
                        >
                          <SelectTrigger><SelectValue placeholder="Assign…" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— Unassign —</SelectItem>
                            {drivers.map((d) => (
                              <SelectItem key={d.id} value={d.id}>
                                {d.name} · {d.status.replace("_", " ")} · load {driverLoad.get(d.id) ?? 0}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/app/vehicles/${v.id}`}>Open</Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </>
  );
}
